import { ActionItemIn } from './Action';
import { HA_UNIT } from './UnitsOfMeasure';
import { Stock, StockItemOut } from './Warehouse';

export interface ProductOut {
    description?: string;
    sco_unit_measure: string;
    sco_unit_measure_denominator?: string;
    quantity: number;
    area_covered?: number;
    item_id: number;
}

export type GenOrUse = "product_gen" | "product_use" | "harvest" | "delivery_in" | "delivery_out";

export function toProductOut(p: Product): ProductOut {
    return {
        description: p.description || undefined,
        sco_unit_measure: p.unit_measure,
        sco_unit_measure_denominator: p.area_covered ? HA_UNIT : undefined,
        quantity: p.quantity,
        area_covered: p.area_covered || undefined,
        item_id: p.item_id,
    };
}

export interface Product {
    description: string;
    quantity: number;
    item_id: number;
    action_id: number;
    area_covered: number;
    unit_measure: string;
    item_code: string;
    item_name: string;
    physical_quantity: string;
    ds_unit_measure: string;
    sco_unit_measure: string;
    action_resource_id: number;
    resource_type: string;
    unit_measure_denominator: string;
    stocked_lot_number?: string;
    stock?: Stock;
}

export interface ProductAction extends ActionItemIn {
    itemId: number;
    stock: Stock;
    product: ProductType;
}

export interface ProductActionOut {
    description?: string;
    sco_unit_measure: string;
    quantity: number;
    area_covered?: number;
    sco_uom_area?: string;
    item_id: number;
    warehouse_id: number | undefined;
    stocked_lot_id?: number;
    stocked_lot: StockItemOut;
    operation_type: string;
}

export interface Harvest {
    sco_unit_measure: string;
    ds_unit_measure: string;
    quantity: number;
    area_covered: number;
    sco_uom_area: string;
    item_id?: number;
    product?: ProductType;
    warehouse_id?: number;
    physical_quantity?: string;
}
export interface HarvestOut {
    sco_unit_measure: string;
    quantity: number;
    area_covered: number;
    sco_uom_area: string;
    item_id?: number;
    warehouse_id?: number;
}

export interface HarvestIn {
    description: string;
    quantity: number;
    execution_id: number;
    execution_resource_id: number;
    fk_resource_id: number;
    physical_quantity: string;
    area_covered: number;
    unit_measure: string;
    ds_unit_measure: string;
    resource_type: string;
    resource_name: string;
    item_name: string;
    total_cost: number;
    dataset_type: string;
}

export interface ProductType {
    item_id: number;
    item_code: string;
    physical_quantity: string;
    ds_unimis_family: string;
    manufacturer_id: number;
    manufacturer_code: string;
    item_name: string;
    unimis_family: string;
    is_used: number;
    is_shared: number;
    prices: Price[];
    last_update: Date;
    item_note: string;
    company_name: string;
    class_p_code: string;
    class_s_code: string;
    ds_class_code: string;
}

export interface Price {
    currency: string;
    cod_price_type: string;
    pkid: number;
    unit_measure: string;
    ref_price: number;
    sco_currency: string;
    sco_unit_measure: string;
    price_type: string;
    is_cost: number;
    is_revenue: number;
    entity_type: string;
    entity_id: number;
}

export function toProductAction(p: Product): ProductAction {
    return {
        area_covered: p.area_covered,
        description: p.description,
        ds_unit_measure: p.ds_unit_measure,
        quantity: p.quantity,
        unit_measure: p.sco_unit_measure,
        physical_quantity: p.physical_quantity,
        product: {
            item_id: p.item_id,
            item_name: p.item_name,
            item_code: p.item_code,
        } as ProductType,
        itemId: p.item_id
    } as ProductAction
}

export function toProductActionOut(p: ProductAction, type: GenOrUse): ProductActionOut {
    return {
        description: p.description || undefined,
        sco_unit_measure: p.unit_measure,
        quantity: p.quantity,
        area_covered: p.area_covered || undefined,
        sco_uom_area: p.area_covered ? HA_UNIT : undefined,
        item_id: p.product?.item_id,
        warehouse_id: p.stock?.warehouse_id,
        stocked_lot_id: p.stock?.stocked_lot_id,
        stocked_lot: {
            lot_number: p.stock?.stocked_lot_number?p.stock?.stocked_lot_number:""
        },
        operation_type: type === "product_gen" || type === "delivery_in" ? "LOAD_ACTIVITY" : "UNLOAD_ACTIVITY"
    }
}

export function toHarvest(p: ProductAction): Harvest {
    return {
        area_covered: p.area_covered,
        item_id: p.product.item_id,
        product: p.product,
        quantity: p.quantity,
        sco_unit_measure: p.unit_measure,
        ds_unit_measure: p.ds_unit_measure,
        sco_uom_area: "HA",
        warehouse_id: p.stock?.warehouse_id,
        physical_quantity: p.physical_quantity
    }
}

export function toHarvestOut(h: Harvest): HarvestOut{
    return {
        area_covered: h.area_covered,
        quantity: h.quantity,
        sco_unit_measure: h.sco_unit_measure,
        sco_uom_area: h.sco_uom_area,
        item_id: h.item_id,
        warehouse_id: h.warehouse_id
    }
}

export function toDeliveryProductAction(p: ProductAction): ProductAction {
    return {
        product: p.product,
        description: p.description || undefined,
        sco_unit_measure: p.sco_unit_measure,
        ds_unit_measure: p.ds_unit_measure,
        unit_measure: p.sco_unit_measure,
        quantity: p.quantity,
        itemId: p.itemId,
        stock: {
            ...p.stock,
            warehouse_id: undefined,
        },
        area_covered: p.area_covered,
        execution_id: p.execution_id,
        dataset_type: p.dataset_type,
        execution_resource_id: p.execution_id,
        physical_quantity: p.physical_quantity,
        fk_resource_id: p.execution_id,
        resource_type: p.resource_type,
        resource_name: p.resource_name,
        total_cost: p.execution_id,
    }
}
// export function toDeliveryProductActionOut(p: ProductAction): any {
//     return {
//         description: p.description || undefined,
//         sco_unit_measure: p.sco_unit_measure,
//         quantity: p.quantity,
//         item_id: p.product?.item_id,
//         warehouse_id: undefined,
//         stocked_lot_id: p.stock?.stocked_lot_id,
//         stocked_lot: {
//             lot_number: p.stock?.stocked_lot_number
//         },
//         operation_type: "LOAD_ACTIVITY"
//     }
// }

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function copyUnitMeasure(list: {[key: string]: any}[], from: string, to: string){
    if(!list){
        return;
    }
    list.forEach(r => r[to] = r[from]);
}
